import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Compass, RefreshCw, ExternalLink, CheckCircle2, AlertCircle, Clock, Loader2, RotateCcw, X } from 'lucide-react';

const STATUS_BADGE = {
  published: { color: 'bg-green-600', icon: CheckCircle2, label: 'Publié' },
  pending: { color: 'bg-yellow-600', icon: Clock, label: 'En attente' },
  publishing: { color: 'bg-blue-600', icon: Loader2, label: 'Publication…' },
  failed: { color: 'bg-red-600', icon: AlertCircle, label: 'Échec' },
  skipped: { color: 'bg-gray-500', icon: X, label: 'Ignoré' },
};

const formatParis = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'short', timeStyle: 'short' });
  } catch (e) { return iso; }
};

const Discover = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/discover/batches`);
      setBatches(r.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement Discover');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const it = setInterval(load, 60000);
    return () => clearInterval(it);
  }, [load]);

  const retry = async (batchId, subjectId) => {
    try {
      await axios.post(`${API}/discover/batch/${batchId}/subjects/${subjectId}/retry`);
      toast.success('Sujet replanifié dans 5 min');
      load();
    } catch (e) {
      toast.error("Erreur retry");
    }
  };

  const skip = async (batchId, subjectId) => {
    if (!window.confirm('Marquer ce sujet comme ignoré (ne sera pas publié) ?')) return;
    try {
      await axios.post(`${API}/discover/batch/${batchId}/subjects/${subjectId}/skip`);
      toast.success('Sujet ignoré');
      load();
    } catch (e) {
      toast.error("Erreur skip");
    }
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-testid="discover-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Compass className="w-7 h-7" />
          <div>
            <h1 className="text-2xl font-bold">Discover (ScoutMediaAI)</h1>
            <p className="text-sm text-gray-500">Articles week-end auto-publiés sur EconomieMatin · 1/h</p>
          </div>
        </div>
        <Button onClick={load} variant="outline" disabled={refreshing} data-testid="discover-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Rafraîchir
        </Button>
      </div>

      {batches.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            Aucun batch Discover reçu pour l'instant.<br />
            <span className="text-xs">Les lots arrivent samedi & dimanche entre 06h et 23h Paris.</span>
          </CardContent>
        </Card>
      )}

      {batches.map((b) => (
        <Card key={b.batch_id} data-testid={`batch-${b.batch_id}`}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-base font-mono">{b.batch_id}</CardTitle>
                <p className="text-xs text-gray-500 mt-1">
                  Reçu : {formatParis(b.received_at)} · Source : {b.source} · Site : {b.site}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Badge className="bg-green-600">{b.published_count} publiés</Badge>
                {b.failed_count > 0 && <Badge className="bg-red-600">{b.failed_count} échecs</Badge>}
                {b.pending_count > 0 && <Badge className="bg-yellow-600">{b.pending_count} en attente</Badge>}
                <Badge variant="outline">{b.status}</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-2">
              {(b.subjects || []).map((s) => {
                const meta = STATUS_BADGE[s.status] || { color: 'bg-gray-400', icon: Clock, label: s.status };
                const Icon = meta.icon;
                return (
                  <div
                    key={s.subject_id}
                    className="flex items-start justify-between gap-2 p-3 rounded border border-gray-200 dark:border-gray-700"
                    data-testid={`subject-${b.batch_id}-${s.subject_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${meta.color} text-white`}>
                          <Icon className={`w-3 h-3 mr-1 ${s.status === 'publishing' ? 'animate-spin' : ''}`} />
                          {meta.label}
                        </Badge>
                        {s.theme && <Badge variant="outline" className="text-xs">{s.theme}</Badge>}
                      </div>
                      <p className="text-sm font-medium truncate">
                        {s.final_title || s.title_fr || '(sans titre)'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {s.status === 'published'
                          ? `Publié à ${formatParis(s.published_at)}`
                          : s.status === 'failed'
                            ? `Erreur : ${s.error || 'inconnue'}`
                            : `Programmé à ${formatParis(s.scheduled_for)}`}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {s.wordpress_url && (
                        <a
                          href={s.wordpress_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                          data-testid={`view-wp-${s.subject_id}`}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" /> WP
                        </a>
                      )}
                      {(s.status === 'failed') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => retry(b.batch_id, s.subject_id)}
                          data-testid={`retry-${s.subject_id}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> Retry
                        </Button>
                      )}
                      {['failed', 'pending'].includes(s.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => skip(b.batch_id, s.subject_id)}
                          data-testid={`skip-${s.subject_id}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default Discover;
